import { LogOut, RefreshCw, Search } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { PageNavigationBar } from "@/lib/components/navigation/page-navigation-bar";

interface SalonSelectorNavBarProps {
  onReload: () => void;
  onLogout: () => void;
  isRefreshing: boolean;
  showSearch: boolean;
  searchQuery: string;
  onSearchChange: (query: string) => void;
  filteredSalonsCount: number;
}

export function SalonSelectorNavBar({
  onReload,
  onLogout,
  isRefreshing,
  showSearch,
  searchQuery,
  onSearchChange,
  filteredSalonsCount,
}: SalonSelectorNavBarProps) {
  // Search section - desktop trailing element
  const desktopSearchElement = showSearch ? (
    <div className="hidden lg:flex items-center gap-2">
      <div className="relative">
        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 w-4 h-4 z-10 pointer-events-none" />
        <Input
          data-testid="salon-selector-search-desktop"
          placeholder="Search salons..."
          value={searchQuery}
          onChange={(e) => onSearchChange(e.target.value)}
          className="pl-10 w-64 bg-white/70 backdrop-blur-sm border-slate-200 focus:border-blue-300 focus:ring-blue-300"
        />
      </div>
      {searchQuery && (
        <span
          data-testid="salon-selector-search-results-count-desktop"
          className="text-sm text-slate-500 whitespace-nowrap"
        >
          {filteredSalonsCount} salon{filteredSalonsCount === 1 ? "" : "s"}
        </span>
      )}
    </div>
  ) : undefined;

  // Mobile search section
  const mobileSearchElement = showSearch ? (
    <div className="lg:hidden max-w-md mx-auto">
      <div className="relative">
        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 w-4 h-4 z-10 pointer-events-none" />
        <Input
          data-testid="salon-selector-search-mobile"
          placeholder="Search salons, cities, or franchises..."
          value={searchQuery}
          onChange={(e) => onSearchChange(e.target.value)}
          className="pl-10 bg-white/70 backdrop-blur-sm border-slate-200 focus:border-blue-300 focus:ring-blue-300"
        />
      </div>
      {searchQuery && (
        <p
          data-testid="salon-selector-search-results-count-mobile"
          className="text-sm text-slate-500 mt-2 text-center"
        >
          {filteredSalonsCount} salon
          {filteredSalonsCount === 1 ? "" : "s"}
        </p>
      )}
    </div>
  ) : undefined;

  return (
    <PageNavigationBar
      title="Select Your Salon"
      leadElement={[
        <Button
          key="refresh"
          data-testid="salon-selector-refresh-button"
          onClick={onReload}
          variant="ghost"
          size="sm"
          disabled={isRefreshing}
          className="flex items-center gap-2"
        >
          <RefreshCw className={`w-4 h-4 ${isRefreshing ? "animate-spin" : ""}`} />
          <span className="hidden sm:inline">{isRefreshing ? "Refreshing..." : "Refresh"}</span>
        </Button>,
      ]}
      trailingElements={[
        desktopSearchElement,
        <Button
          key="logout"
          data-testid="salon-selector-logout-button"
          onClick={onLogout}
          variant="ghost"
          size="sm"
          className="flex items-center gap-2 text-slate-600 hover:text-slate-900"
        >
          <LogOut className="w-4 h-4" />
          <span className="hidden sm:inline">Sign Out</span>
        </Button>,
      ].filter(Boolean)}
      bottomElement={mobileSearchElement}
    />
  );
}
